import React, { useState } from "react";
import { type UserTransaction } from "utils/types/user.type";

interface TransactionTableProps {
  transactions: UserTransaction[];
  isLoading: boolean;
}

export default function TransactionTable({
  transactions,
  isLoading,
}: TransactionTableProps) {
  if (isLoading) {
    return (
      <div className="table-wrapper max-h-[38rem] overflow-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-800"></div>
        </div>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="table-wrapper max-h-[38rem] overflow-auto">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-black-700 text-lg font-semibold">
            No transactions found
          </p>
          <p className="text-black-700 text-sm mt-2">
            Try adjusting your filters or search criteria
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-wrapper max-h-[38rem] overflow-auto">
      <table className="w-full">
        <thead className="sticky top-0 bg-white z-10">
          <tr>
            <th className="rounded-l-lg border border-black-800/30 border-r-0 py-5 px-5 text-left">
              Date & Time
            </th>
            <th className="py-5 px-5 border-y border-black-800/30 text-left">
              User Details
            </th>
            <th className="py-5 px-5 border-y border-black-800/30 text-center">
              Amount
            </th>
            <th className="py-5 px-5 border-y border-black-800/30 text-center">
              Network
            </th>
            <th className="py-5 px-5 border-y border-black-800/30 text-center">
              Status
            </th>
            <th className="rounded-r-lg border border-black-800/30 border-l-0 py-5 px-5 text-center">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => (
            <TableRow key={transaction.id} transaction={transaction} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableRow({ transaction }: { transaction: UserTransaction }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getTypeColor = (type: string) => {
    const typeMap: Record<string, { bg: string; text: string; badge: string }> = {
      Deposit: { bg: "bg-cyan-300", text: "text-cyan-900", badge: "bg-cyan-800" },
      Withdraw: { bg: "bg-[#FFDEEA]", text: "text-red-900", badge: "bg-[#FF689F]" },
      Buy: { bg: "bg-green-100", text: "text-green-900", badge: "bg-green-600" },
      Sell: { bg: "bg-purple-100", text: "text-purple-900", badge: "bg-purple-600" },
      Swap: { bg: "bg-[#F3F3F3]", text: "text-gray-900", badge: "bg-[#D4D4D4]" },
    };

    // Handle Swap transactions
    if (type.includes("Swap")) {
      return typeMap.Swap || { bg: "bg-[#F3F3F3]", text: "text-gray-900", badge: "bg-[#D4D4D4]" };
    }

    return typeMap[type] || { bg: "bg-gray-100", text: "text-gray-900", badge: "bg-gray-600" };
  };

  const typeColor = getTypeColor(transaction.type);

  return (
    <>
      <tr className={`${typeColor.bg} transition hover:opacity-90`}>
        <td className="rounded-l-lg px-4 border border-black-800/30 border-r-0 py-4">
          <p className="text-black-900 text-xs font-medium">
            {new Date(transaction.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </p>
          <p className="text-black-700 text-xs mt-1">
            {new Date(transaction.createdAt).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </td>
        <td className="py-4 px-4 border-y border-black-800/30">
          <p className="text-black-900 text-xs font-semibold">
            {transaction.user?.email || "N/A"}
          </p>
          {transaction.user?.fullname && (
            <p className="text-black-700 text-xs mt-1">
              {transaction.user.fullname}
            </p>
          )}
        </td>
        <td className="py-4 px-4 border-y border-black-800/30">
          <p className="text-black-900 text-sm text-center font-semibold">
            {Number(transaction.amount ?? 0).toLocaleString(undefined, {
              maximumFractionDigits: 6,
            })}{" "}
            {transaction.coin?.symbol ?? ""}
          </p>
          {transaction.amountInUsd && (
            <p className="text-black-700 text-xs text-center mt-1">
              ${Number(transaction.amountInUsd ?? 0).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </p>
          )}
        </td>
        <td className="py-4 px-4 border-y border-black-800/30">
          <p className="text-black-900 text-xs text-center font-semibold">
            {transaction.coin?.network?.name || transaction.coin?.name || "N/A"}
          </p>
          {transaction.coin?.network?.symbol && (
            <p className="text-black-700 text-xs text-center mt-1">
              {transaction.coin.network.symbol}
            </p>
          )}
        </td>
        <td className="py-4 px-4 border-y border-black-800/30 text-center">
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
              transaction.status === "completed"
                ? "bg-green-500 text-white"
                : transaction.status === "pending"
                ? "bg-yellow-500 text-white"
                : transaction.status === "rejected"
                ? "bg-red-500 text-white"
                : "bg-gray-500 text-white"
            }`}
          >
            {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
          </span>
        </td>
        <td className="rounded-r-lg px-4 border border-black-800/30 border-l-0 text-center py-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            type="button"
            className={`${typeColor.badge} text-white text-xs font-semibold rounded-lg px-4 py-2 hover:opacity-80 transition`}
          >
            {isExpanded ? "Hide Details" : "View Details"}
          </button>
        </td>
      </tr>

      {/* Expanded Details Row */}
      {isExpanded && (
        <tr className={`${typeColor.bg}`}>
          <td
            colSpan={6}
            className="border border-black-800/30 border-t-0 rounded-b-lg p-6"
          >
            <div className="bg-white/50 rounded-lg p-4">
              <h4 className="text-sm font-bold text-black-800 mb-4">
                Transaction Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <DetailItem
                  label="Transaction ID"
                  value={transaction.id}
                />
                {transaction.fromAddress && (
                  <DetailItem
                    label="From Address"
                    value={transaction.fromAddress}
                  />
                )}
                {transaction.toAddress && (
                  <DetailItem
                    label="To Address"
                    value={transaction.toAddress}
                  />
                )}
                {transaction.trxHash && (
                  <DetailItem
                    label="Transaction Hash"
                    value={transaction.trxHash}
                  />
                )}
                <DetailItem
                  label="Network"
                  value={transaction.coin?.network?.name || transaction.coin?.name || "N/A"}
                />
                <DetailItem
                  label="Amount (USD)"
                  value={`$${Number(transaction.amountInUsd ?? 0).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}`}
                />
                <DetailItem
                  label="Wallet ID"
                  value={transaction.walletId}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-black-700 font-medium mb-1">{label}</p>
      <p className="text-sm text-black-900 font-semibold break-all">{value}</p>
    </div>
  );
}

